// Card for a selected fleet in flight (spec 15c §3.5): who, where to, what, and when it arrives.
import { useNavigate } from 'react-router';
import { reinforceLink } from '../../fleetTiming';
import { FLEET_STATUS_LABELS, formatDuration, formatShips, MISSION_LABELS } from '../../labels';
import { fleetEnds, type MapFleet } from '../../map/fleets';
import { PlayerLink } from '../ProfileLinks';
import { Button } from '../ui';

export const MAP_CARD_CLASS =
  'absolute inset-x-2 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-40 hud-cut-sm lg:bottom-2 border border-line bg-panel/95 p-4 shadow-2xl backdrop-blur sm:left-auto sm:w-80';

const heading = 'font-display text-lg font-bold uppercase tracking-[0.08em]';

export function FleetCard({
  galaxyId,
  fleet,
  now,
  systemName,
  onClose,
}: {
  galaxyId: string;
  fleet: MapFleet;
  now: number;
  systemName(id: string | null | undefined): string;
  onClose(): void;
}) {
  const navigate = useNavigate();
  const item = fleet.item;
  const ends = item.relation === 'detected' ? null : fleetEnds(item.fleet);
  const systemLink = (id: string | null | undefined) =>
    id ? (
      <button
        type="button"
        className="underline decoration-dotted underline-offset-2 hover:text-info"
        onClick={() => navigate(`/g/${galaxyId}/systems/${id}`)}
      >
        {systemName(id)}
      </button>
    ) : (
      '—'
    );
  const arrives = (
    <p className="font-mono text-xs tabular-nums text-info">
      Arrives in {formatDuration(fleet.arrivesAt - now)} ·{' '}
      {new Date(fleet.arrivesAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </p>
  );
  return (
    <aside data-map-card="fleet" className={MAP_CARD_CLASS}>
      {item.relation === 'detected' ? (
        <>
          <h2 className={`${heading} text-danger`}>Hostile fleet</h2>
          <p className="text-sm text-muted">
            <PlayerLink
              galaxyId={galaxyId}
              playerId={item.detection.attacker.playerId}
              name={item.detection.attacker.displayName}
            />
            {item.detection.attacker.allianceTag ? ` [${item.detection.attacker.allianceTag}]` : ''}{' '}
            → {systemLink(item.detection.targetSystemId)}
          </p>
          <p className="text-xs text-muted">
            {item.detection.ships
              ? formatShips(item.detection.ships)
              : item.detection.sizeClass
                ? `Size: ${item.detection.sizeClass}`
                : 'Size unknown'}
          </p>
          {arrives}
        </>
      ) : (
        <>
          <h2 className={`${heading} text-text`}>
            {item.fleet.mission
              ? MISSION_LABELS[item.fleet.mission]
              : FLEET_STATUS_LABELS[item.fleet.status]}
          </h2>
          <p className="text-sm text-muted">
            {item.relation === 'own' ? (
              'You'
            ) : (
              <PlayerLink
                galaxyId={galaxyId}
                playerId={item.owner.playerId}
                name={item.owner.displayName}
              />
            )}{' '}
            · {FLEET_STATUS_LABELS[item.fleet.status]}
          </p>
          <p className="text-sm text-muted">
            {ends?.recalled ? 'Recalled' : systemLink(ends?.from)} → {systemLink(ends?.to)}
          </p>
          <p className="text-xs text-muted">{formatShips(item.fleet.ships)}</p>
          {arrives}
        </>
      )}
      <div className="mt-3 flex gap-2">
        {item.relation === 'own' ? (
          <Button className="flex-1" onClick={() => navigate(`/g/${galaxyId}/fleet`)}>
            Open in Fleet
          </Button>
        ) : null}
        {item.relation === 'own' &&
        item.fleet.status === 'in_transit' &&
        (item.fleet.mission === 'attack' || item.fleet.mission === 'raid') &&
        item.fleet.targetSystemId &&
        item.fleet.arrivesAt ? (
          <Button
            variant="secondary"
            onClick={() =>
              navigate(
                reinforceLink(galaxyId, {
                  targetSystemId: item.fleet.targetSystemId as string,
                  mission: item.fleet.mission === 'raid' ? 'raid' : 'attack',
                  arriveAt: item.fleet.arrivesAt as string,
                })
              )
            }
          >
            Reinforce
          </Button>
        ) : null}
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </aside>
  );
}
